// AgentsSection: the right panel's first section in a Chief of Staff chat —
// every sub-agent and workflow the chat launched, listed down the panel in
// chat order (plan design/2026-10-26-chief-of-staff-agents-panel.md, T7). It
// replaced the row of "<type> running..." chips above the composer, which ten
// workflow agents turned into a wall.
//
//   sub-agent   icon, description (or type), status, usage; a click opens the
//               drawer on its conversation
//   workflow    name and "n of m agents"; opens (and starts open while it
//               runs) to one row per agent from the chat's shared run poll,
//               each opening the drawer on that agent's saved conversation.
//               Before its run id is known it shows its summary only.
//
// The list is derived (`deriveDelegatedAgents`), the runs come from the one
// poll AgentChat makes (DelegatedAgentsContext); this component fetches
// nothing. Drawn only when there is something to list.

import React, { useEffect, useRef, useState } from 'react'
import { Box, ButtonBase, Typography } from '@mui/material'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { DelegatedStatusBadge, formatUsage } from './SubagentCard.js'
import { useDelegatedAgents, type AgentRef } from './DelegatedAgentsContext.js'
import { subagentNow, workflowAgentNow, type DelegatedEntry } from '../delegatedAgents.js'
import type { DelegatedStatus, WorkflowAgentInfo, WorkflowRun } from '../types.js'

/**
 * A workflow agent's status: a journal result reads Done (an agent told to
 * reply "failed: …" still reads Done; its text says so); no result while the
 * workflow runs reads Working; no result once it settled takes the
 * workflow's own status.
 */
export function workflowAgentStatus(agent: WorkflowAgentInfo, workflowStatus: DelegatedStatus): DelegatedStatus {
  if (agent.result != null || agent.status === 'completed') return 'completed'
  return workflowStatus === 'running' ? 'running' : workflowStatus
}

/** "1 of 3 agents" for a run; null before the run is known. */
export function workflowAgentCount(run: WorkflowRun | undefined, workflowStatus: DelegatedStatus): string | null {
  if (!run) return null
  const done = run.agents.filter(a => workflowAgentStatus(a, workflowStatus) === 'completed').length
  const total = run.agents.length
  return `${done} of ${total} ${total === 1 ? 'agent' : 'agents'}`
}

/**
 * Every agent, not every launch: a direct sub-agent is one, a workflow is
 * the agents of its run (one while its run is not known yet).
 */
export function countAgents(agents: DelegatedEntry[], runs: ReadonlyMap<string, WorkflowRun> | undefined): { total: number; working: number } {
  let total = 0
  let working = 0
  for (const entry of agents) {
    if (entry.kind === 'subagent') {
      total += 1
      if (entry.info.status === 'running') working += 1
      continue
    }
    const run = entry.info.runId ? runs?.get(entry.info.runId) : undefined
    if (!run || run.agents.length === 0) {
      total += 1
      if (entry.info.status === 'running') working += 1
      continue
    }
    total += run.agents.length
    working += run.agents.filter(a => workflowAgentStatus(a, entry.info.status) === 'running').length
  }
  return { total, working }
}

/**
 * The line beside the composer's orb: what the main agent is doing (its
 * activity label, while a turn streams) and, while any delegated agent works,
 * how many and the newest step one of them took. Empty: say nothing.
 */
export function composerStatusLine(
  mainLabel: string | undefined,
  agents: DelegatedEntry[],
  runs: ReadonlyMap<string, WorkflowRun> | undefined,
): string {
  const parts: string[] = []
  if (mainLabel) parts.push(mainLabel.replace(/\.\.\.$/, ''))
  const { working } = countAgents(agents, runs)
  if (working > 0) {
    // The freshest step among the running agents: a workflow agent's carries
    // its time; a sub-agent's is live, so it stands in when none has a time.
    let fresh: { who: string; now: string; at: string } | null = null
    for (const entry of agents) {
      if (entry.kind === 'subagent') {
        const now = entry.info.status === 'running' ? subagentNow(entry.info) : undefined
        if (now && !fresh) fresh = { who: entry.info.description || 'Sub-agent', now, at: '' }
        continue
      }
      const run = entry.info.runId ? runs?.get(entry.info.runId) : undefined
      for (const a of run?.agents ?? []) {
        if (workflowAgentStatus(a, entry.info.status) !== 'running' || !a.last_step) continue
        const now = workflowAgentNow(a)
        const at = a.last_step.at ?? ''
        if (now && (!fresh || at > fresh.at)) fresh = { who: a.label || 'Agent', now, at }
      }
    }
    parts.push(`${working} ${working === 1 ? 'agent' : 'agents'} working${fresh ? ` · ${fresh.who}: ${fresh.now}` : ''}`)
  }
  return parts.join(' · ')
}

/** Who made a file, for the Artifacts list: the agent's label or task, or nothing for the main agent. */
export function artifactAgentLabel(
  artifact: { agentId?: string; parentToolUseId?: string },
  agents: DelegatedEntry[] | undefined,
  runs: ReadonlyMap<string, WorkflowRun> | undefined,
): string | undefined {
  if (artifact.parentToolUseId) {
    const e = agents?.find(x => x.toolCallId === artifact.parentToolUseId)
    if (e?.kind === 'subagent') return e.info.description || e.info.agentType || 'Sub-agent'
  }
  if (artifact.agentId && runs) {
    for (const run of runs.values()) {
      const a = run.agents.find(x => x.id === artifact.agentId)
      if (a) return a.label || 'Workflow agent'
    }
  }
  return undefined
}

export interface AgentsSectionProps {
  agents: DelegatedEntry[]
  /** Scroll to and open this workflow on every new nonce (a workflow card's click). */
  focusWorkflow?: { toolCallId: string; nonce: number } | null
}

const rowSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1.5,
  py: 0.75,
  textAlign: 'left',
  justifyContent: 'flex-start',
  '&:hover': { backgroundColor: 'action.hover' },
  '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'info.main', outlineOffset: -2 },
} as const

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const

function Row({ testId, icon, title, detail, status, indent, onClick, ariaExpanded, rowRef, selected }: {
  testId: string
  icon: React.ReactNode
  title: string
  detail?: string
  status: DelegatedStatus
  indent?: boolean
  onClick?: () => void
  ariaExpanded?: boolean
  rowRef?: React.Ref<HTMLButtonElement>
  /** Open in the drawer. */
  selected?: boolean
}) {
  return (
    <ButtonBase
      ref={rowRef}
      data-testid={testId}
      data-state={status}
      onClick={onClick}
      disabled={!onClick}
      aria-expanded={ariaExpanded}
      aria-current={selected ? 'true' : undefined}
      sx={{ ...rowSx, pl: indent ? 4 : 1.5, ...(selected && { backgroundColor: 'action.selected', boxShadow: 'inset 3px 0 0 0', color: 'info.main' }) }}
    >
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary', ...ellipsis }}>{title}</Typography>
        {detail && <Typography title={detail} sx={{ fontSize: 11, color: 'text.secondary', ...ellipsis }}>{detail}</Typography>}
      </Box>
      <DelegatedStatusBadge status={status} />
    </ButtonBase>
  )
}

function WorkflowRows({ entry, run, runError, onRetry, open, onToggle, openAgent, rowRef, selectedAgentId }: {
  entry: Extract<DelegatedEntry, { kind: 'workflow' }>
  run: WorkflowRun | undefined
  /** The last read of the run failed: said under the row, with Retry. */
  runError?: string
  onRetry?: () => void
  /** The agent of this run open in the drawer. */
  selectedAgentId?: string
  open: boolean
  onToggle: () => void
  openAgent?: (ref: AgentRef) => void
  rowRef: React.Ref<HTMLButtonElement>
}) {
  const { info, toolCallId } = entry
  const runId = info.runId
  const count = runId ? workflowAgentCount(run, info.status) : null
  // Before the run id (or the run) is known there is nothing to open into.
  const expandable = !!runId && !!run && run.agents.length > 0
  const detail = count ?? info.summary ?? info.description
  const chevron = expandable
    ? (open ? <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />)
    : <AccountTreeOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
  return (
    <>
      <Row
        testId="agents-workflow-row"
        rowRef={rowRef}
        icon={chevron}
        title={`Workflow: ${info.name || info.description || 'workflow'}`}
        detail={count && info.summary ? `${count} · ${info.summary}` : detail}
        status={info.status}
        onClick={expandable ? onToggle : undefined}
        ariaExpanded={expandable ? open : undefined}
      />
      {expandable && open && run!.agents.map(agent => (
        <Row
          key={agent.id}
          testId="agents-row"
          indent
          icon={<SmartToyOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
          title={agent.label || agent.id}
          detail={workflowAgentNow(agent) ?? agent.phase}
          status={workflowAgentStatus(agent, info.status)}
          onClick={openAgent ? () => openAgent({ kind: 'workflow-agent', toolCallId, runId: runId!, agentId: agent.id }) : undefined}
          selected={agent.id === selectedAgentId}
        />
      ))}
      {runError && (
        <Typography data-testid="agents-workflow-error" sx={{ pl: 4.5, pr: 1.5, pb: 0.75, fontSize: 12, color: 'text.secondary' }}>
          {runError}
          {onRetry && (
            <ButtonBase onClick={onRetry} sx={{ ml: 0.75, fontSize: 12, color: 'info.main', verticalAlign: 'baseline' }}>
              Retry
            </ButtonBase>
          )}
        </Typography>
      )}
    </>
  )
}

export default function AgentsSection({ agents, focusWorkflow = null }: AgentsSectionProps) {
  const api = useDelegatedAgents()
  const runs = api?.runs
  // A workflow's open state is the reader's once they have toggled it;
  // until then it is open while the workflow runs.
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  const workflowRowRefs = useRef(new Map<string, HTMLButtonElement>())

  // A workflow card's click: open that workflow. Render-phase guard (one
  // state change per nonce, nothing to clean up), so it is open in the same
  // paint the panel opens with.
  const lastFocusNonceRef = useRef<number | null>(null)
  if (focusWorkflow && focusWorkflow.nonce !== lastFocusNonceRef.current) {
    lastFocusNonceRef.current = focusWorkflow.nonce
    if (toggled[focusWorkflow.toolCallId] !== true) {
      setToggled(t => ({ ...t, [focusWorkflow.toolCallId]: true }))
    }
  }
  // ...and bring it into view: an imperative side effect tied to that one
  // trigger, the nonce.
  const focusNonce = focusWorkflow?.nonce
  const focusId = focusWorkflow?.toolCallId
  useEffect(() => {
    if (focusNonce == null || !focusId) return
    workflowRowRefs.current.get(focusId)?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    // The nonce is the trigger; the id rides with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce])

  if (agents.length === 0) return null

  const { total, working } = countAgents(agents, runs)
  return (
    <Box data-testid="agents-section" sx={{ py: 1, borderBottom: '1px solid #e5e7eb' }}>
      <Typography sx={{ fontWeight: 600, fontSize: 14, px: 1.5, mb: 0.5 }}>
        Agents ({working > 0 ? `${working} of ${total} working` : total})
      </Typography>
      {agents.map(entry => {
        if (entry.kind === 'subagent') {
          const { info, toolCallId } = entry
          return (
            <Row
              key={toolCallId}
              testId="agents-row"
              icon={<SmartToyOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              title={info.description || info.agentType || 'Sub-agent'}
              detail={subagentNow(info) ?? (info.usage ? formatUsage(info.usage) : undefined)}
              status={info.status}
              onClick={api ? () => api.openAgent({ kind: 'subagent', toolCallId }) : undefined}
              selected={api?.selected?.kind === 'subagent' && api.selected.toolCallId === toolCallId}
            />
          )
        }
        const id = entry.toolCallId
        const open = toggled[id] ?? entry.info.status === 'running'
        return (
          <WorkflowRows
            key={id}
            entry={entry}
            run={entry.info.runId ? runs?.get(entry.info.runId) : undefined}
            runError={entry.info.runId ? api?.runErrors?.get(entry.info.runId) : undefined}
            onRetry={entry.info.runId && api?.retryRun ? () => api.retryRun!(entry.info.runId!) : undefined}
            open={open}
            onToggle={() => setToggled(t => ({ ...t, [id]: !open }))}
            openAgent={api?.openAgent}
            selectedAgentId={api?.selected?.kind === 'workflow-agent' && api.selected.toolCallId === id ? api.selected.agentId : undefined}
            rowRef={(el: HTMLButtonElement | null) => {
              if (el) workflowRowRefs.current.set(id, el)
              else workflowRowRefs.current.delete(id)
            }}
          />
        )
      })}
    </Box>
  )
}
